import React, { Reducer, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { PostingPlanView, PostingPlanViewProps } from './PostingPlan.view';
import { ThirdNavLeftButtons, ThirdNavRightButtons } from './ThirdLevelNavigation';
import { useThirdNavChildren } from '../../../hooks/useThirdNavChildren';
import { RefetchQueryFunction, Void } from 'models/basic-objects';
import {
  DeleteTimeslotMutationFn,
  GetCategoriesQueryResult,
  GetSocialProfilesQueryResult,
  GetTimeslotsQuery,
  GetTimeslotsQueryResult,
  GetTimeslotsQueryVariables,
  PostItemFragment
} from '../../../models/graphql';
import { useForm } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { TimeslotFilterMenuViewProps } from './TimeslotFilterMenu/TimeslotFilterMenu.view';
import {
  getFilterLocalStorageValue,
  PostingPlanFiltersEnum,
  setFilterLocalStorage
} from '../../../helpers/filterStorageHelpers';
import { useUserData } from '../../../hooks/useUserData';
import { getNumberOfWorkspaces } from '../../TopNav/helpers/Workspace';
import {
  ThirdNavCenterContent,
  ThirdNavLeftPostingPlanProps,
  ThirdNavRightPostingPlanProps
} from './ThirdLevelNavigation/ThirdLevelNavigation.view';
import { PostingPlanHelpers } from './postingPlanHelpers';
import { TimeslotFilterAction, TimeslotFilterType } from '../../../models/calendar';
import { CategoryType } from '../../../components/filters/CategoryFilterGroup/CategoryFilterGroup.controller';
import { ProfileType } from '../../../components/filters/SocialProfileFilterGroup/SocialProfileFilterGroup.controller';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { AppContext } from '../../../providers/AppProvider';
import { GetTimeslots_workspace_timeslots } from '../../../models/legacy-graphql';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePauseSettings } from '../../../hooks/usePauseSettings';
import { WhatInputType } from 'what-input';
import useWhatInput from 'react-use-what-input';
import { InformationBanner } from './PostingPlanCloneCalendar/InformationBanner.view';

export type ProfileCategoryLocationState = {
  category: PostItemFragment['category'];
  profile: PostItemFragment['socialProfile'];
} | null;

export type PostingPlanControllerProps = Omit<
  PostingPlanViewProps,
  | 'loading'
  | 'openCreateEditTimeslotModal'
  | 'setOpenCreateEditTimeslotModal'
  | 'openBulkDeleteTimeslotsModal'
  | 'setOpenBulkDeleteTimeslotsModal'
  | 'filteredTimeslots'
  | 'selectedTimeslot'
  | 'profiles'
  | 'categories'
  | 'categoryFilter'
  | 'categoryFilterHandler'
  | 'filters'
  | 'dispatchFilters'
  | 'profilesFilteredTimeslots'
  | 'categoriesFilteredTimeslots'
  | 'timeslotFilterMenuProps'
  | 'openDeleteTimeslotWarningModal'
  | 'setOpenDeleteTimeslotWarningModal'
  | 'numberOfWorkspaces'
  | 'dateClicked'
  | 'loadingCalendar'
  | 'setLoadingCalendar'
  | 'openFilterModal'
  | 'setOpenFilterModal'
  | 'addTimeslotHandler'
  | 'editTimeslotHandler'
  | 'cloneTimeslotHandler'
  | 'deleteTimeslotHandler'
  | 'applyMobileFilters'
  | 'resetMobileFilters'
  | 'refetchTimeslots'
  | 'onCreateEditTimeslotModalClose'
  | 'timeslotToClone'
  | 'setTimeslotToClone'
> & {
  socialProfileResults: GetSocialProfilesQueryResult;
  categoryResults: GetCategoriesQueryResult;
  timeslotsResults: GetTimeslotsQueryResult;
  deleteTimeslot: DeleteTimeslotMutationFn;
};

export const isLastTimeslotForSubQueue = (
  timeslotToDelete: GetTimeslots_workspace_timeslots,
  timeslots: GetTimeslots_workspace_timeslots[]
): boolean => {
  const profilesCount: number[] = [];

  let isLast = false;

  const timeslotsWithSameCategory = timeslots.filter(t => t.category.id === timeslotToDelete?.category.id);
  timeslotsWithSameCategory.forEach(timeslot => {
    timeslot.socialProfiles.forEach(profile => {
      if (timeslotToDelete.socialProfiles.find(p => p.id === profile.id)) {
        // check to see if a count has started for the profile id
        if (!(Number(profile.id) in profilesCount)) {
          profilesCount[Number(profile.id)] = 0;
        }

        profilesCount[Number(profile.id)]++;
      }
    });
  });

  profilesCount.forEach(value => {
    if (!isLast) {
      if (value === 1) {
        isLast = true;
      }
    }
  });

  return isLast;
};

export const isProfileCategoryLocationState = (state: unknown): state is ProfileCategoryLocationState => {
  return state !== null && typeof state === 'object' && 'category' in state && 'profile' in state;
};

const filtersInitialState = (selectedWorkspaceId: string | null): TimeslotFilterType => ({
  profiles: getFilterLocalStorageValue(
    selectedWorkspaceId || '',
    'PostingPlanFilters',
    PostingPlanFiltersEnum.profileFilter
  ),
  categories: getFilterLocalStorageValue(
    selectedWorkspaceId || '',
    'PostingPlanFilters',
    PostingPlanFiltersEnum.categoryFilter
  ),
  showOutOfSeason:
    getFilterLocalStorageValue(
      selectedWorkspaceId || '',
      'PostingPlanFilters',
      PostingPlanFiltersEnum.showOutOfSeason
    ) === 'true',
  tempProfiles: '',
  tempCategories: ''
});

export const PostingPlanController = (props: PostingPlanControllerProps): JSX.Element => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const [filters, dispatchFilters] = useReducer<Reducer<TimeslotFilterType, TimeslotFilterAction>>(
    PostingPlanHelpers.timeslotFilterReducer,
    filtersInitialState(selectedWorkspaceId)
  );
  //const { state: filters, dispatch: setFilters } = useContext(TimeslotFiltersContext);
  const { userData } = useUserData();
  const [numberOfWorkspaces, setNumberOfWorkspaces] = useState<number>(0);
  const [openCreateEditTimeslotModal, setOpenCreateEditTimeslotModal] = useState<boolean>(false);
  const [openBulkDeleteTimeslotsModal, setOpenBulkDeleteTimeslotsModal] = useState<boolean>(false);
  const [openDeleteTimeslotWarningModal, setOpenDeleteTimeslotWarningModal] = useState<boolean>(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState<GetTimeslots_workspace_timeslots | undefined>(undefined);
  const [dateClicked, setDateClicked] = useState<[number, number] | undefined>(undefined);
  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [loadingCalendar, setLoadingCalendar] = useState<boolean>(false);
  const [timeslotToClone, setTimeslotToClone] = useState<GetTimeslots_workspace_timeslots | null>(null);
  const { workspacePublishingPaused: isQueuePaused } = usePauseSettings();
  const formInstance = useForm({
    ...FormOptions
  });

  const intentType: WhatInputType = useWhatInput('intent');

  const editTimeslotHandler = useCallback((timeslot: GetTimeslots_workspace_timeslots) => {
    setSelectedTimeslot(timeslot);
    setOpenCreateEditTimeslotModal(true);
  }, []);

  const addTimeslotHandler: Void<[number, number]> = useCallback((param?) => {
    setDateClicked(param);

    setSelectedTimeslot(undefined);
    setOpenCreateEditTimeslotModal(true);
  }, []);

  const cloneTimeslotHandler = (timeslot: GetTimeslots_workspace_timeslots) => {
    const clonedTimeslot: GetTimeslots_workspace_timeslots = {
      __typename: 'Timeslot',
      id: `clone-${timeslot.id}`,
      day: timeslot.day,
      time: timeslot.time,
      category: timeslot.category,
      socialProfiles: timeslot.socialProfiles,
      onboardingContent: timeslot.onboardingContent
    };

    if (intentType === 'touch') {
      editTimeslotHandler(clonedTimeslot);
    } else {
      setTimeslotToClone(clonedTimeslot);
    }
  };

  const deleteTimeslotHandler = (timeslot: GetTimeslots_workspace_timeslots, lastItemApproval?: boolean) => {
    const isLast = isLastTimeslotForSubQueue(timeslot, props.timeslotsResults.data?.workspace.timeslots || []);

    if (!isLast || (isLast && lastItemApproval)) {
      props
        .deleteTimeslot({
          variables: { timeslotId: timeslot.id },
          optimisticResponse: {
            deleteTimeslot: true
          },
          update: cache => {
            cache.evict({ id: cache.identify({ id: timeslot.id, __typename: 'Timeslot' }) });
            cache.gc();

            setSelectedTimeslot(undefined);
          }
        })
        .catch(e => {
          props.timeslotsResults
            .refetch?.({ workspaceId: selectedWorkspaceId || undefined })
            .catch(triggerErrorToast('Error Fetching Timeslot'));
          triggerErrorToast('Error deleting the timeslot')(e);
        });
    } else {
      setSelectedTimeslot(timeslot);
      setOpenDeleteTimeslotWarningModal(true);
    }
  };

  const categories = useMemo(() => {
    return (
      props.categoryResults.data?.workspace.categories.filter(category => {
        const isInSeason = !category.seasonallyPaused;

        if (
          filters.categories &&
          selectedWorkspaceId &&
          (!props.categoryResults.data?.workspace.categories.filter(c => c.id === filters.categories).length ||
            (category.archived && category.id === filters.categories))
        ) {
          filters.categories = '';
          setFilterLocalStorage(selectedWorkspaceId, 'PostingPlanFilters', PostingPlanFiltersEnum.categoryFilter, '');
        }

        return !category.archived ? filters.showOutOfSeason || isInSeason === !filters.showOutOfSeason : false;
      }) || []
    );
  }, [filters, props.categoryResults.data?.workspace.categories, selectedWorkspaceId]);

  const timeslots = useMemo(() => {
    return props.timeslotsResults.data?.workspace.timeslots.filter(timeslot => {
      return categories?.find(c => c.id === timeslot.category.id);
    });
  }, [props.timeslotsResults.data?.workspace.timeslots, categories]);

  const filteredTimeslotsByProfiles = useMemo(() => {
    return timeslots?.filter(timeslot => {
      let isInSet = true;

      if (
        filters.profiles &&
        selectedWorkspaceId &&
        !props.socialProfileResults.data?.workspace.socialProfiles.filter(p => p.id === filters.profiles).length
      ) {
        filters.profiles = '';
        setFilterLocalStorage(selectedWorkspaceId, 'PostingPlanFilters', PostingPlanFiltersEnum.profileFilter, '');
      }

      if (isInSet && filters.profiles) {
        isInSet = timeslot.socialProfiles.findIndex(profile => profile.id === filters.profiles) > -1;
      }

      return isInSet;
    });
  }, [timeslots, filters, selectedWorkspaceId, props.socialProfileResults.data?.workspace.socialProfiles]);

  const filteredTimeslotsByCategories = useMemo(() => {
    return timeslots?.filter(timeslot => {
      let isInSet = true;

      if (isInSet && filters.categories) {
        isInSet = timeslot.category.id === filters.categories;
      }

      return isInSet;
    });
  }, [filters, timeslots]);

  const combinedFilteredTimeslots = useMemo(() => {
    return timeslots?.filter(timeslot => {
      let isInSet = true;

      if (isInSet && filters.categories) {
        isInSet = timeslot.category.id === filters.categories;
      }
      if (isInSet && filters.profiles) {
        isInSet = timeslot.socialProfiles.findIndex(profile => profile.id === filters.profiles) > -1;
      }

      return isInSet;
    });
  }, [filters, timeslots]);

  const mapSocialProfilesToProfileType = useCallback((): ProfileType[] => {
    // ...
    const profileTypeSet: ProfileType[] = [];
    props.socialProfileResults.data?.workspace.socialProfiles.forEach(profile => {
      const provider = profilesHelpers.mapOAuthFromTypename(profile.__typename);
      const network = profilesHelpers.mapNetworkFromTypename(profile.__typename);
      const generic = profile.__typename === 'GenericSocialProfile' ? profile.genericProfile : undefined;

      profileTypeSet.push({
        id: profile.id,
        provider: provider,
        network: network,
        genericProfile: generic,
        connected: profile.connected,
        photoUrl: profile.photoUrl,
        displayName: profile.displayName,
        onboardingContent: profile.onboardingContent
      });
    });

    return profileTypeSet;
  }, [props.socialProfileResults.data?.workspace.socialProfiles]);

  // Global components expect their own types to be passed
  const mapCategoriesToCategoryType = useCallback((): CategoryType[] => {
    const categoryTypeSet: CategoryType[] = [];
    categories.forEach(cat => {
      // We never want archived categories
      if (!cat.archived) {
        categoryTypeSet.push({
          id: cat.id,
          name: cat.name,
          archived: cat.archived,
          color: cat.color,
          seasonal: cat.seasonal,
          seasonalStartDate: cat.seasonalStartDate,
          seasonalEndDate: cat.seasonalEndDate,
          seasonallyPaused: cat.seasonallyPaused
        });
      }
    });

    return categoryTypeSet;
  }, [categories]);

  const timeslotFilterMenuProps: TimeslotFilterMenuViewProps = useMemo(
    () => ({
      formInstance,
      timeslots: timeslots || [],
      profiles: mapSocialProfilesToProfileType(),
      categories: mapCategoriesToCategoryType(),
      profilesFilteredTimeslots: filteredTimeslotsByProfiles || [],
      categoriesFilteredTimeslots: filteredTimeslotsByCategories || [],
      loading: loadingCalendar,
      filters,
      dispatchFilters
    }),
    [
      filteredTimeslotsByCategories,
      filteredTimeslotsByProfiles,
      filters,
      formInstance,
      loadingCalendar,
      mapCategoriesToCategoryType,
      mapSocialProfilesToProfileType,
      timeslots
    ]
  );

  const thirdNavChildren = useMemo(() => {
    // if we have a timeslotToClone, we do not use thirdNavChildren
    if (timeslotToClone)
      return {
        center: <InformationBanner buttonAction={() => setTimeslotToClone(null)} />
      };

    const thirdNavLeftProps: ThirdNavLeftPostingPlanProps = {
      ...timeslotFilterMenuProps,
      isQueuePaused,
      open: openFilterModal,
      setOpen: setOpenFilterModal
    };

    const thirdNavRightProps: ThirdNavRightPostingPlanProps = {
      addTimeslotHandler,
      setOpenBulkDeleteTimeslotsModal
    };

    return {
      left: <ThirdNavLeftButtons {...thirdNavLeftProps} />,
      center: <ThirdNavCenterContent isQueuePaused={isQueuePaused} />,
      right: <ThirdNavRightButtons {...thirdNavRightProps} />
    };
  }, [timeslotToClone, timeslotFilterMenuProps, isQueuePaused, openFilterModal, addTimeslotHandler]);

  useThirdNavChildren(thirdNavChildren);

  const setFiltersFromLocalStorage = (selectedWorkspaceId: string) => {
    const profilesId = getFilterLocalStorageValue(
      selectedWorkspaceId,
      'PostingPlanFilters',
      PostingPlanFiltersEnum.profileFilter
    );
    const categoriesId = getFilterLocalStorageValue(
      selectedWorkspaceId,
      'PostingPlanFilters',
      PostingPlanFiltersEnum.categoryFilter
    );
    const showOutOfSeason =
      getFilterLocalStorageValue(selectedWorkspaceId, 'PostingPlanFilters', PostingPlanFiltersEnum.showOutOfSeason) ===
      'true';
    if (profilesId) {
      dispatchFilters({ type: 'profiles', payload: profilesId });
    }
    if (categoriesId) {
      dispatchFilters({ type: 'categories', payload: categoriesId });
    }
    dispatchFilters({ type: 'showOutOfSeason', payload: showOutOfSeason });
  };

  const applyMobileFilters = (): void => {
    const payload: TimeslotFilterType = {
      tempCategories: '',
      tempProfiles: '',
      profiles: filters.tempProfiles || (filters.tempProfiles !== null ? filters.profiles : ''),
      categories: filters.tempCategories || (filters.tempCategories !== null ? filters.categories : ''),
      showOutOfSeason: filters.showOutOfSeason
    };
    dispatchFilters({
      type: 'applyTempFilters',
      payload
    });
    setOpenFilterModal(false);
  };

  const resetMobileFilters = (): void => {
    dispatchFilters({ type: 'resetAll' });
    setOpenFilterModal(false);
  };

  // This will set the browser local storage for the filters object stored there
  // we use 3 different useEffect in order to avoid manipulating and setting all props each time
  useEffect(() => {
    if (selectedWorkspaceId) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        'PostingPlanFilters',
        PostingPlanFiltersEnum.categoryFilter,
        filters.categories
      );
    }
  }, [filters.categories, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        'PostingPlanFilters',
        PostingPlanFiltersEnum.profileFilter,
        filters.profiles
      );
    }
  }, [filters.profiles, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        'PostingPlanFilters',
        PostingPlanFiltersEnum.showOutOfSeason,
        `${filters.showOutOfSeason}`
      );
    }
  }, [filters.showOutOfSeason, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setFiltersFromLocalStorage(selectedWorkspaceId);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (userData) {
      const workspacesCounter = getNumberOfWorkspaces(userData.organizations);
      setNumberOfWorkspaces(workspacesCounter);
    }
  }, [userData]);

  const location = useLocation();
  const navigate = useNavigate();
  const profileCategoryFromLocation = isProfileCategoryLocationState(location.state) ? location.state : null;
  const [timeslotModalOpenedForUnqueuedPost, setTimeslotModalOpenedForUnqueuedPost] = useState(false);
  useEffect(() => {
    if (profileCategoryFromLocation && !timeslotModalOpenedForUnqueuedPost) {
      // Always set the hour to be 12pm (noon) and the first day of week.
      setDateClicked([12, 1]);
      setOpenCreateEditTimeslotModal(true);
      setTimeslotModalOpenedForUnqueuedPost(true);
      dispatchFilters({ type: 'categories', payload: profileCategoryFromLocation.category.id });
      dispatchFilters({ type: 'profiles', payload: profileCategoryFromLocation.profile.id });
      window.history.replaceState({ state: {} }, '');
    }
  }, [profileCategoryFromLocation, timeslotModalOpenedForUnqueuedPost, formInstance, navigate]);

  const refetchTimeslots: RefetchQueryFunction<GetTimeslotsQuery, GetTimeslotsQueryVariables> | undefined =
    props.timeslotsResults.refetch;

  const viewProps: PostingPlanViewProps = {
    ...props,
    selectedTimeslot,
    filteredTimeslots: combinedFilteredTimeslots || [],
    profiles: mapSocialProfilesToProfileType(),
    categories: mapCategoriesToCategoryType(),
    timeslotFilterMenuProps,
    loading:
      !props.categoryResults.called ||
      props.categoryResults.loading ||
      !props.socialProfileResults.called ||
      props.socialProfileResults.loading ||
      !props.timeslotsResults.called ||
      props.timeslotsResults.loading,
    openCreateEditTimeslotModal,
    setOpenCreateEditTimeslotModal,
    openBulkDeleteTimeslotsModal,
    setOpenBulkDeleteTimeslotsModal,
    openDeleteTimeslotWarningModal,
    setOpenDeleteTimeslotWarningModal,
    filters,
    dispatchFilters,
    numberOfWorkspaces,
    dateClicked,
    loadingCalendar,
    setLoadingCalendar,
    openFilterModal,
    setOpenFilterModal,
    addTimeslotHandler,
    editTimeslotHandler,
    cloneTimeslotHandler,
    deleteTimeslotHandler,
    applyMobileFilters,
    resetMobileFilters,
    refetchTimeslots,
    timeslotToClone,
    setTimeslotToClone
  };

  return <PostingPlanView {...viewProps} />;
};
